import * as React from 'react';
import styled from 'styled-components';

import { BREAKPOINTS, COLORS, ComponentTextStyle } from '../styles';
import { SystemIcons } from '../icons';
import { IconButton } from '../Button';
import { getButtonStyle } from './styles';
import { Size } from '../types';
import { ComponentMStyling, ComponentResponsive } from '../styles';
import { HyperLink } from '../HyperLink';
import { StyledLink } from '../HyperLink';
import { hasWindow } from '../utils/utils';

/**
 * Styles for <Banner />
 */
export type StyleBannerProps = BannerProps & { $type: string; linkVisited: string; linkFocused: string };

export const BannerContainerStyles = styled.div<StyleBannerProps>`
  background: ${(props: StyleBannerProps) => (props.$type ? props.$type : COLORS.correct_100)};
  min-height: 48px;
  display: flex;
  color: ${COLORS.black};
  margin-top: ${(props: StyleBannerProps) => (props.bottom ? 'auto' : '0')};
  border-radius: 4px;

  button:last-child {
    line-height: 0;
    margin: 0 0 0 8px;
    ${BREAKPOINTS.MEDIUM} {
      margin: 0 0 0 16px;
    }
    ${BREAKPOINTS.LARGE} {
      margin: 0 0 0 32px;
    }
    &.small {
      margin: 0 0 0 8px;
    }
    &.medium {
      margin: 0 0 0 16px;
    }
    &.large {
      margin: 0 0 0 32px;
    }
  }

  ${StyledLink} {
    &:link {
      color: ${(props) => props.link};
    }
    &:visited {
      color: ${(props) => props.linkVisited};
    }
    &:focus,
    &:active {
      background-color: ${(props) => props.linkFocused};
      color: ${COLORS.white};
    }
  }

  &.full-width-banner {
    margin: 8px;
  }
`;

export const BannerCenterStyles = styled.div`
  ${ComponentMStyling(ComponentTextStyle.Regular, null)}

  display: flex;
  width: 100%;
  margin: 0 16px;
  ${BREAKPOINTS.MEDIUM} {
    margin: 0 32px;
  }
  ${BREAKPOINTS.LARGE} {
    margin: 0 56px;
  }
  &.small {
    margin: 0 16px;
  }
  &.medium {
    margin: 0 32px;
  }
  &.large {
    margin: 0 64px;
  }

  > svg {
    flex-shrink: 0;
    margin: 12px 0;
  }

  .full-width-banner & {
    margin: 0 16px;

    ${BREAKPOINTS.MEDIUM} {
      margin: 0 32px;
    }
    ${BREAKPOINTS.LARGE} {
      margin: 0 64px;
    }

    &.small {
      margin: 0 16px;
    }

    &.medium {
      margin: 0 32px;
    }

    &.large {
      margin: 0 64px;
    }
  }
`;

export const BannerCloseButtonWrapperStyles = styled.div`
  margin: 0 0 0 auto;
`;

export const BannerCloseButtonContainerStyles = styled.div<{ $type?: string }>`
  display: contents;
  ${(props) => (props.$type ? getButtonStyle(props.$type) : null)};
`;

export const BannerContentWrapperStyles = styled.div`
  display: flex;
  align-items: center;
  margin: 12px 0;
  &:not(:first-child) {
    margin-left: 8px;
  }
`;

export type BannerProps = React.HTMLAttributes<HTMLDivElement> & {
  size?: Size.Small | Size.Medium | Size.Large;
  type?: 'warning' | 'critical' | 'positive' | 'neutral';
  link?: string;
  linkText?: string;
  linkAction?: (ev: React.MouseEvent) => void;
  hover?: string;
  bottom?: boolean;
  testId?: string;
  icon?: React.ReactNode;
  onClose?: () => void;
  noIcon?: boolean;
  fullWidth?: boolean;
};

const Banner: React.FunctionComponent<BannerProps> = ({
  size,
  type = 'neutral',
  children,
  bottom,
  testId,
  linkText,
  link,
  onClose,
  icon,
  noIcon,
  linkAction,
  fullWidth,
  className,
  ...rest
}) => {
  const [width, setWidth] = React.useState<number>(hasWindow() ? window.innerWidth : -1);
  React.useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  let bannerParams = {
    typeColor: COLORS.primary_100,
    accentColor: COLORS.primary_700,
    hoverColor: COLORS.primary_20,
    closeIconColor: COLORS.primary_500,
    icon: SystemIcons.Tip,
    containerType: type,
    focusBgColor: COLORS.primary_200,
    linkVisited: COLORS.primary_600,
    linkFocused: COLORS.primary_800,
  };
  switch (type) {
    case 'warning':
      bannerParams.typeColor = COLORS.warning_100;
      bannerParams.accentColor = COLORS.warning_700;
      bannerParams.focusBgColor = COLORS.warning_200;
      bannerParams.hoverColor = COLORS.warning_20;
      bannerParams.icon = SystemIcons.Help;
      bannerParams.closeIconColor = COLORS.warning_500;
      bannerParams.linkVisited = COLORS.warning_800;
      bannerParams.linkFocused = COLORS.warning_800;
      break;
    case 'critical':
      bannerParams.typeColor = COLORS.critical_100;
      bannerParams.accentColor = COLORS.critical_700;
      bannerParams.focusBgColor = COLORS.critical_200;
      bannerParams.hoverColor = COLORS.critical_20;
      bannerParams.icon = SystemIcons.TechnicalWarning;
      bannerParams.closeIconColor = COLORS.critical_500;
      bannerParams.linkVisited = COLORS.critical_800;
      bannerParams.linkFocused = COLORS.critical_800;
      break;
    case 'positive':
      bannerParams.typeColor = COLORS.correct_100;
      bannerParams.accentColor = COLORS.correct_700;
      bannerParams.focusBgColor = COLORS.correct_200;
      bannerParams.hoverColor = COLORS.correct_20;
      bannerParams.icon = SystemIcons.ThumbsUp;
      bannerParams.closeIconColor = COLORS.correct_500;
      bannerParams.linkVisited = COLORS.correct_800;
      bannerParams.linkFocused = COLORS.correct_800;
      break;
  }

  const cls = `${className ?? ''} ${fullWidth ? 'full-width-banner' : ''}`;

  return (
    <BannerContainerStyles
      data-testid={testId}
      $type={bannerParams.typeColor}
      linkFocused={bannerParams.linkFocused}
      linkVisited={bannerParams.linkVisited}
      link={bannerParams.accentColor}
      hover={bannerParams.hoverColor}
      bottom={bottom}
      className={cls}
      role="note"
      {...rest}>
      <BannerCenterStyles style={{ color: bannerParams.accentColor }} className={size}>
        {icon ? icon : noIcon ? null : <bannerParams.icon color={bannerParams.accentColor} size="24px" />}
        <BannerContentWrapperStyles>
          <ComponentResponsive size={size} width={width} color={bannerParams.accentColor}>
            {children}&nbsp;
            {link && linkText && (
              <HyperLink
                id={`${Math.floor(Math.random() * 999999999999)}_BannerLink`}
                onClick={(e) => {
                  if (linkAction) {
                    e.preventDefault();
                    linkAction(e);
                  }
                }}
                href={link}
                variant="default">
                {linkText}
              </HyperLink>
            )}
          </ComponentResponsive>
        </BannerContentWrapperStyles>
        {onClose && (
          <BannerCloseButtonWrapperStyles>
            <BannerCloseButtonContainerStyles $type={bannerParams.containerType}>
              <IconButton variant="secondary" shape="circular" useTransparentBackground focusBackgroundColor={bannerParams.focusBgColor} action={() => onClose()}>
                <SystemIcons.Close color={bannerParams.closeIconColor} size="24px" />
              </IconButton>
            </BannerCloseButtonContainerStyles>
          </BannerCloseButtonWrapperStyles>
        )}
      </BannerCenterStyles>
    </BannerContainerStyles>
  );
};

export default Banner;
